import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const VERDICT_COPY = {
  paid: "Stripe shows this transfer was made. Applying records it as paid.",
  resend: "Stripe shows nothing yet and the original request can be safely replayed with its original key.",
  wait: "Too late to replay safely and too early to void. Nothing can be applied yet.",
  void: "Stripe shows no transfer for this payout. Applying voids the attempt so it can be released again.",
  conflict: "Stripe evidence is ambiguous. Nothing can be applied — investigate in the Stripe dashboard.",
};
const APPLY_LABEL = { paid: "Record as paid", resend: "Replay original request", void: "Void attempt" };
const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
const ids = (v) => (Array.isArray(v) ? v.join(", ") : v || "—");

export const StuckPayoutDialog = ({ row, onClose, onResolved }) => {
  const op = row?.stuck_payout_op;
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const inspect = () => {
    if (!op) return;
    setData(null); setError("");
    api.post(`/admin/payouts/operations/${op.pop_id}/resolve`, { action: "inspect" })
      .then(({ data: d }) => setData(d))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Inspection failed"));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(inspect, [op?.pop_id]);

  const apply = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/payouts/operations/${op.pop_id}/resolve`, { action: "apply", expected_verdict: data.verdict });
      toast.success("Payout operation resolved");
      onResolved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Resolve failed");
      inspect();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!op} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="stuck-payout-dialog" className="max-w-md bg-[#FDFBF7]">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Resolve stuck payout</DialogTitle></DialogHeader>
        {op && (
          <div className="space-y-3 text-sm">
            <div className="rounded-md bg-[#F5F2EB] p-4 space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-[color:var(--text-muted)]">Operation</span><span className="font-mono">{op.pop_id}</span></div>
              <div className="flex justify-between"><span className="text-[color:var(--text-muted)]">State</span><span data-testid="stuck-op-state">{op.state}</span></div>
              <div className="flex justify-between"><span className="text-[color:var(--text-muted)]">Started</span><span>{fmt(op.created_at)}</span></div>
            </div>
            {error && <div className="text-[#D9583A] text-xs" data-testid="stuck-inspect-error">{error}</div>}
            {!data && !error && <div className="text-xs text-[color:var(--text-muted)]">Checking Stripe…</div>}
            {data && (
              <div className="space-y-2" data-testid="stuck-inspection">
                <div className="eyebrow">Stripe verdict</div>
                <div className="font-serif text-xl capitalize" data-testid="stuck-verdict">{data.verdict}</div>
                <p className="text-xs text-[color:var(--text-muted)]">{VERDICT_COPY[data.verdict]}</p>
                {data.verdict === "wait" && data.void_available_at && (
                  <p className="text-xs" data-testid="stuck-void-available-at">Void becomes available at <strong>{fmt(data.void_available_at)}</strong>.</p>
                )}
                {data.evidence && (
                  <div className="text-[11px] text-[color:var(--text-muted)] space-y-0.5">
                    <div data-testid="stuck-evidence-group">Booking transfer search: <span className="font-mono">{ids(data.evidence.group_search)}</span></div>
                    <div data-testid="stuck-evidence-destination">Vendor account search: <span className="font-mono">{ids(data.evidence.destination_search)}</span></div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-full" data-testid="stuck-cancel-btn">Close</Button>
          {data && APPLY_LABEL[data.verdict] && (
            <Button data-testid="stuck-apply-btn" onClick={apply} disabled={busy} className="rounded-full bg-[#1A1A1A] hover:bg-black text-white">
              {busy ? "Applying…" : APPLY_LABEL[data.verdict]}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
